import { Button } from "components/DesignSystem/Button/Button";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { Search } from "components/DesignSystem/Search/Search";
import { TableUI } from "components/design/TableUI";
import { useFilters } from "hooks/useFilter";
import { useModal } from "hooks/useModal";
import { pluralize } from "utils/pluralize";
import { useMemo, useState } from "react";
import { AlarmRule, useGetAlarmRulesQuery } from "store/apis/alarm";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import ThreeDots from "static/images/ThreeDots.svg";
import { CreateEditAlarmRuleModal } from "./CreateEditAlarmRuleModal";
import {
  DATE_TYPE_LABELS,
  DEADLINE_DAY_SUFFIX,
  FIXED_DATE_TYPE,
  monthLabel,
} from "constants/alarmRules";

const columnHelper = createColumnHelper<AlarmRule>();

const deadlineDisplay = (rule: AlarmRule) => {
  const { date_type, deadline_date, deadline_day, deadline_month } = rule;

  const isRelative = date_type !== FIXED_DATE_TYPE;

  if (isRelative) {
    const dateTypeLabel = DATE_TYPE_LABELS[date_type] ?? "deadline";
    const daySuffix =
      deadline_day != null && deadline_day !== 0
        ? `, ${DEADLINE_DAY_SUFFIX[deadline_day]}`
        : "";

    if (deadline_month != null && deadline_month < 0) {
      const monthsStr = pluralize(Math.abs(deadline_month), "month", "months");
      return `${monthsStr} before ${dateTypeLabel.toLowerCase()}${daySuffix}`;
    }

    if (deadline_month != null && deadline_month > 0) {
      const monthsStr = pluralize(deadline_month, "month", "months");
      return `${monthsStr} after ${dateTypeLabel.toLowerCase()}${daySuffix}`;
    }

    if (daySuffix) {
      return `${dateTypeLabel}, ${DEADLINE_DAY_SUFFIX[deadline_day!]}`;
    }

    return "—";
  }

  const month = deadline_month != null ? monthLabel(deadline_month) : null;

  if (deadline_date != null && month) return `${month} ${deadline_date}`;
  if (deadline_day === 3 && month) return `${month} — last day`;
  if (deadline_day === 3) return "Last day of the month";
  if (deadline_day === 2 && month) return `${month} 15`;
  if (deadline_day === 1 && month) return `${month} 1`;
  if (month) return month;
  return "—";
};

export const StateDeadlineRulesTab = () => {
  const { values, updateFilter, updateMultipleFilters } = useFilters({
    initialValue: { SEARCH: "", PAGE_NUM: "1" },
    useQueryParams: true,
  });
  const [selectedRule, setSelectedRule] = useState<AlarmRule | undefined>();
  const createModal = useModal();
  const editModal = useModal();

  const queryArgs = useMemo(
    () => ({
      search_term: values.SEARCH || undefined,
      page_num: Number(values.PAGE_NUM),
    }),
    [values.SEARCH, values.PAGE_NUM]
  );

  const { data, isLoading, isFetching } = useGetAlarmRulesQuery(queryArgs);
  const rules = data?.rules ?? [];
  const {
    total_pages = 1,
    current_page = 1,
    total_count = 0,
    per_page = 20,
  } = data ?? {};

  const handleEditClick = (rule: AlarmRule) => {
    setSelectedRule(rule);
    editModal.open();
  };

  const columns = [
    columnHelper.accessor("us_state_name", {
      header: "State",
      size: 15,
      cell: (info) => info.getValue() ?? "—",
    }),
    columnHelper.accessor("display_name", {
      header: "Display name",
      size: 25,
      cell: (info) => info.getValue() ?? info.row.original.name,
    }),
    columnHelper.accessor("formation_type_name", {
      header: "Formation type",
      size: 12,
      cell: (info) => info.getValue() ?? "All",
    }),
    columnHelper.accessor("date_type", {
      header: "Date type",
      size: 18,
      cell: (info) =>
        DATE_TYPE_LABELS[info.getValue()] ?? String(info.getValue()),
    }),
    columnHelper.display({
      id: "deadline",
      header: "Deadline",
      size: 15,
      cell: ({ row }) => deadlineDisplay(row.original),
    }),
    columnHelper.accessor("approx_price", {
      header: "Price",
      size: 5,
      cell: (info) => {
        const price = info.getValue();
        return price ? `$${price}` : "—";
      },
    }),
    columnHelper.display({
      id: "actions",
      header: "",
      size: 2,
      cell: ({ row }) => (
        <Dropdown.Root>
          <Dropdown.Trigger asChild>
            <Button customType="ghost_icon" size="xsmall">
              <img src={ThreeDots} alt="Actions" />
            </Button>
          </Dropdown.Trigger>
          <Dropdown.Portal>
            <Dropdown.Content align="end">
              <Dropdown.Item onClick={() => handleEditClick(row.original)}>
                Edit
              </Dropdown.Item>
            </Dropdown.Content>
          </Dropdown.Portal>
        </Dropdown.Root>
      ),
    }),
  ];

  const table = useReactTable({
    data: rules,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="t-flex t-flex-col t-gap-4">
      <div className="t-flex t-flex-col t-gap-4">
        <div className="t-flex t-items-center t-justify-between">
          <p className="t-m-0 t-text-subtitle-sm">Deadline Rules</p>
          <Button customType="primary" size="small" onClick={createModal.open}>
            Create new rule
          </Button>
        </div>
        <div className="t-flex t-gap-4">
          <Search
            placeholder="Search by state or rule name..."
            value={values.SEARCH}
            onChange={(e) =>
              updateMultipleFilters({ SEARCH: e.target.value, PAGE_NUM: "1" })
            }
            className="t-w-[18%] focus:t-w-3/6 t-transition-all t-self-start"
          />
          <div className="t-flex t-justify-end t-self-start t-ml-auto">
            <PaginationHandler
              page_num={current_page}
              per_page={per_page}
              total_count={total_count}
              total_pages={total_pages}
              onPageNumChange={(page) => updateFilter("PAGE_NUM", String(page))}
              isLoading={isFetching}
            />
          </div>
        </div>
      </div>

      <TableUI table={table} isLoading={isLoading} />

      <CreateEditAlarmRuleModal
        open={createModal.isOpen}
        onClose={createModal.close}
      />

      <CreateEditAlarmRuleModal
        open={editModal.isOpen}
        onClose={editModal.close}
        rule={selectedRule}
      />
    </div>
  );
};
